import { useState, useEffect, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar/Sidebar.js';
import PageHeader from '../PageHeader';
import NotificationSnackbar from '../NotificationSnackbar';
import ProfileCompletionModal from '../ProfileCompletionModal';
import devNotificationSound from '../../../assets/sounds/devnotification.mp3';
import { getCookie } from '../../../infrastructure/utils/cookieStorage';
import { initiateNotificationSocket } from '../../../infrastructure/services/notificationService';
import { profileService } from '../../../infrastructure/services/profileService';

export default function AppLayout({ children }) {
    const navigate = useNavigate();
    const [sidebarExpanded, setSidebarExpanded] = useState(() => {
        if (typeof window === 'undefined') return true;
        const isMobile = window.innerWidth <= 768;
        if (isMobile) return false;

        const stored = window.localStorage.getItem('sidebarExpanded');
        if (stored === null) return true;
        return stored === 'true';
    });

    const [activeNotification, setActiveNotification] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [notificationAnimState, setNotificationAnimState] = useState('closed');
    const audioCtxRef = useRef(null);
    // Profile completion states
    const [showProfileCompletionModal, setShowProfileCompletionModal] = useState(false);
    const [isSavingProfileCompletion, setIsSavingProfileCompletion] = useState(false);

    useEffect(() => {
        const checkProfile = async () => {
            try {
                const profile = await profileService.getProfile();
                if (!profile.province || !profile.district) {
                    setShowProfileCompletionModal(true);
                }
            } catch (e) {
                console.error('Failed to verify profile completeness:', e);
            }
        };
        checkProfile();
    }, []);

    const handleSaveProfileCompletion = async (provinceName, districtName) => {
        if (!provinceName || !districtName) return;
        setIsSavingProfileCompletion(true);
        try {
            const profile = await profileService.getProfile();
            const formData = new FormData();
            formData.append('full_name', profile.fullName || profile.displayName || 'Student');
            formData.append('province', provinceName);
            formData.append('district', districtName);

            await profileService.updateProfile(formData);
            setShowProfileCompletionModal(false);
        } catch (e) {
            console.error('Failed to complete profile:', e);
        } finally {
            setIsSavingProfileCompletion(false);
        }
    };



    const getAudioContext = () => {
        if (typeof window === 'undefined') return null;
        if (!audioCtxRef.current) {
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (AudioContextClass) {
                audioCtxRef.current = new AudioContextClass();
            }
        }
        return audioCtxRef.current;
    };

    // Auto-unlock audio context on first user interaction to satisfy browser autoplay policies
    useEffect(() => {
        const unlock = () => {
            try {
                const ctx = getAudioContext();
                if (ctx && ctx.state === 'suspended') {
                    ctx.resume().then(() => {
                        cleanup();
                    });
                } else if (ctx && ctx.state === 'running') {
                    cleanup();
                }
            } catch (e) {
                console.warn('AudioContext unlock failed:', e);
            }
        };

        const cleanup = () => {
            window.removeEventListener('click', unlock);
            window.removeEventListener('keydown', unlock);
            window.removeEventListener('mousedown', unlock);
            window.removeEventListener('touchstart', unlock);
        };

        window.addEventListener('click', unlock);
        window.addEventListener('keydown', unlock);
        window.addEventListener('mousedown', unlock);
        window.addEventListener('touchstart', unlock);

        return cleanup;
    }, []);

    // Play synthesized notification sound helper (no network dependence)
    const playNotificationSound = useCallback(() => {
        try {
            const isDev = process.env.NODE_ENV === 'development';
            const isUat = typeof window !== 'undefined' && (
                window.location.hostname.includes('uat') ||
                window.location.hostname.includes('localhost') ||
                window.location.hostname.includes('127.0.0.1')
            );

            if (isDev || isUat) {
                const audio = new Audio(devNotificationSound);
                audio.play().catch(err => {
                });
            } else {
                const ctx = getAudioContext();
                if (!ctx) return;

                // Play a double chime: note 1 (C5) then note 2 (E5)
                const playTone = (freq, time, duration) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();

                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(freq, time);

                    gain.gain.setValueAtTime(0, time);
                    gain.gain.linearRampToValueAtTime(0.25, time + 0.04);
                    gain.gain.exponentialRampToValueAtTime(0.0001, time + duration);

                    osc.connect(gain);
                    gain.connect(ctx.destination);

                    osc.start(time);
                    osc.stop(time + duration);
                };

                if (ctx.state === 'suspended') {
                    ctx.resume().then(() => {
                        const now = ctx.currentTime;
                        playTone(523.25, now, 0.25); // C5
                        playTone(659.25, now + 0.10, 0.40); // E5
                    }).catch(err => {
                    });
                } else {
                    const now = ctx.currentTime;
                    playTone(523.25, now, 0.25); // C5
                    playTone(659.25, now + 0.10, 0.40); // E5
                }
            }
        } catch (e) {
            console.error('Failed to play notification sound:', e);
        }
    }, []);

    // Auto-dismiss the popup (slide out after 60s total, unmount after 60.5s)
    useEffect(() => {
        if (activeNotification) {
            setShowPopup(true);

            const slideOutTimer = setTimeout(() => {
                setShowPopup(false);
            }, 60000);

            const unmountTimer = setTimeout(() => {
                setActiveNotification(null);
            }, 60500);

            return () => {
                clearTimeout(slideOutTimer);
                clearTimeout(unmountTimer);
            };
        }
    }, [activeNotification]);

    // Dispatch event to coordinate hiding other alerts (like sidebar warnings) when popup is active
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('notification-popup:toggle', { detail: { open: showPopup } }));
    }, [showPopup]);

    // Handle the morphing animation state (dot to expanded)
    useEffect(() => {
        if (showPopup && activeNotification) {
            setNotificationAnimState('dot');
            const timer = setTimeout(() => {
                setNotificationAnimState('expanded');
            }, 1500);
            return () => clearTimeout(timer);
        } else {
            setNotificationAnimState('closed');
        }
    }, [showPopup, activeNotification]);

    // WebSocket connection for real-time notifications using the service folder
    useEffect(() => {
        const token = getCookie('student_auth_token') || getCookie('authToken') || sessionStorage.getItem('student_auth_token') || sessionStorage.getItem('authToken');
        if (!token) return;

        const socket = initiateNotificationSocket(
            token,
            (data) => {
                setActiveNotification(data);
                playNotificationSound();
                window.dispatchEvent(new CustomEvent('notification:received', { detail: data }));
            },
            () => {
                // When authenticated or reconnected, trigger a refresh event
                window.dispatchEvent(new CustomEvent('notification:refresh'));
            }
        );

        return () => {
            if (socket) {
                socket.disconnect();
            }
        };
    }, [playNotificationSound]);

    // Persist sidebar state
    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem('sidebarExpanded', String(sidebarExpanded));
    }, [sidebarExpanded]);

    return (
        <Box sx={{
            '--sidebar-width': {
                xs: sidebarExpanded ? '240px' : '60px',
                sm: sidebarExpanded ? '265px' : '65px',
                md: sidebarExpanded ? '300px' : '86px',
            }
        }}>
            {/* Sidebar */}
            <Sidebar onExpansionChange={setSidebarExpanded} isExpanded={sidebarExpanded} />

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'margin-left 0.3s ease',
                    marginLeft: 'var(--sidebar-width)',
                    minHeight: '100vh',
                    p: { xs: 2, sm: 2, md: 2 }
                }}
            >
                {/* Header */}
                <PageHeader onMenuClick={() => window.dispatchEvent(new Event('sidebar:toggle'))} />

                {/* Page Content */}
                <Box sx={{ flexGrow: 1, mt: 1 }}>
                    {children}
                </Box>
            </Box>

            {/* Notification Popup Card from common folder */}
            <NotificationSnackbar
                open={showPopup}
                onClose={() => setShowPopup(false)}
                isExpanded={sidebarExpanded}
                isScrolledDown={false}
                notificationAnimState={notificationAnimState}
                setNotificationAnimState={setNotificationAnimState}
                activeAlert="exam"
                setIsAlertOpen={setShowPopup}
                activeNotification={activeNotification}
                navigate={navigate}
            />



            {/* Mandatory Profile Completion Dialog */}
            <ProfileCompletionModal
                open={showProfileCompletionModal}
                isSaving={isSavingProfileCompletion}
                onSave={handleSaveProfileCompletion}
            />
        </Box>
    );
}
